import Component from "../component";
import Urls from "../../configuration/urls.config";
import { loadScript } from "../../utils/load_script";
import Selectors from "../../configuration/selectors.config";
import { AssetFeatureResponse } from "../../types/stores";
import { replace, debounce, getMarkerIcon } from "../../utils/utils";
import { getLocale, getLocaleLang } from "../../helpers/locale";
import { getConfig } from "../../configuration/config";
export interface IMapComponent {
    woosmapPublicKey: string;
    mapOptions: woosmap.map.MapOptions;
    nearbyLocation?: woosmap.map.LatLngLiteral;
    selectedStore?: AssetFeatureResponse;
    stores?: woosmap.map.GeoJSONFeature[];
    query?: string;
    padding: woosmap.map.Padding;
    userCurrentPosition?: woosmap.map.LatLngLiteral;
    zoom?: number;
    filter?: string;
    isHighlightMode?: boolean;
}

export enum MapComponentEvents {
    STORES_CHANGED = "stores_changed",
    STORE_SELECTED = "store_selected",
    STORE_UNSELECTED = "store_unselected",
    MAP_READY = "map_ready",
    MAP_IDLE = "map_idle",
    MAP_BOUNDS_CHANGED = "map_bounds_changed",
    MAP_PANNED = 'map_pan',
}

export default class MapComponent extends Component<IMapComponent> {
    public map!: woosmap.map.Map;
    private bounds_listener!: woosmap.map.MapEventListener;
    private storeMarkers: woosmap.map.Marker[] = [];
    private selectedStoreMarker!: woosmap.map.Marker | undefined;

    init(): void {
        this.$element = document.createElement("div");
        this.$element.id = Selectors.mapWrapperID;
        this.$target.appendChild(this.$element);
    }

    render(): void {
        if (this.state && this.$element) {
            loadScript({
                url: Urls.mapJS,
                params: { key: this.state.woosmapPublicKey, language: getLocaleLang().toLowerCase() },
                override: true
            })
                .then(() => {
                    this.initMapView();
                })
                .catch((error) => {
                    console.error(
                        replace(getLocale().errors.loadingLibrary, { 'library': "Woosmap Map JS SDK script" }),
                        error
                    );
                });
        }
    }

    initMapView(): void {
        this.map = new woosmap.map.Map(
            Selectors.mapWrapperID,
            Object.assign({}, this.state.mapOptions, { styles: getConfig().map.mapStyleOptions })
        );
        woosmap.map.event.addListenerOnce(this.map, "idle", () => {
            this.$element.classList.add("mapReady");
            this.emit(MapComponentEvents.MAP_IDLE);
        });
        this.bounds_listener = woosmap.map.event.addListener(this.map, "bounds_changed", debounce(() => {
            const bounds = this.map.getBounds();
            this.emit(MapComponentEvents.MAP_BOUNDS_CHANGED, bounds);
        }, 50));

        this.on(MapComponentEvents.STORES_CHANGED, () => {
            this.handleStores();
        });
        this.on(MapComponentEvents.STORE_SELECTED, () => {
            this.selectStore();
        });
        this.on(MapComponentEvents.STORE_UNSELECTED, () => {
            this.selectStore();
        });
        this.emit(MapComponentEvents.MAP_READY);
        this.on(MapComponentEvents.MAP_PANNED, (direction) => {
            this.panMap(direction);
        })
    }

    handleSelectedStore(store: AssetFeatureResponse): void {
        if (!this.state.selectedStore) {
            this.setState({ selectedStore: store }, true, () => {
                this.emit(MapComponentEvents.STORE_SELECTED, this.state.selectedStore, this.state.stores);
            });
        }
    }
    private renderStoreMarker(element: woosmap.map.GeoJSONFeature): void {
        const feature = element as AssetFeatureResponse;
        const type = feature.properties?.types?.[0] ?? "JR";
        const LatLngPoint = element.geometry as woosmap.map.GeoJSONPoint;
        const cluster = type === "JR" && feature.properties.user_properties?.cluster != null
            ? String(feature.properties.user_properties.cluster) : undefined;
        const klub = type === "McFIT"
            ? feature.properties.user_properties?.kLUB === "True" : undefined;
        const marker = new woosmap.map.Marker({
            position: { lat: LatLngPoint.coordinates[1], lng: LatLngPoint.coordinates[0] },
            icon: getMarkerIcon(type, false, cluster, klub),
            title: feature.properties.name,
            clickable: !this.state.isHighlightMode
        });
        marker.set("store_id", feature.properties.store_id);
        marker.set("brand", type);
        marker.set("cluster", cluster);
        marker.set("kLUB", klub);
        if (this.state.isHighlightMode) {
            marker.setIcon(getMarkerIcon(type, true, cluster, klub));
            marker.cursor = "default";
        } else {
            this.onStoreMarkerClicked(marker, type, feature);
        }
        this.storeMarkers.push(marker);
        marker.setMap(this.map);
    }

    handleStores(): void {
        if (this.state.stores) {
            if (this.state.stores.length > 0) {
                this.removeBoundsListener();
                if (this.storeMarkers.length > 0) {
                    this.storeMarkers.forEach((marker) => {
                        marker.setMap(null);
                    });
                    this.storeMarkers = [];
                }
                this.state.stores.forEach((element) => this.renderStoreMarker(element));

                const bounds = new woosmap.map.LatLngBounds();

                this.state.stores.forEach((feature) => {
                    const LatLngPoint = feature.geometry as woosmap.map.GeoJSONPoint;
                    bounds.extend({ lat: LatLngPoint.coordinates[1], lng: LatLngPoint.coordinates[0] });
                });
                if (this.state.nearbyLocation) {
                    const nearBy = this.state.nearbyLocation;
                    bounds.extend(nearBy);
                }
                if (!this.state.nearbyLocation) {
                    if (this.state.stores.length === 1) {
                        const feature: AssetFeatureResponse = this.state.stores[0] as AssetFeatureResponse;
                        const storeLocation = feature.geometry.coordinates;
                        this.map.flyTo({
                            center: { lat: storeLocation[1], lng: storeLocation[0] },
                            zoom: this.state.zoom,
                            padding: this.state.padding,
                            animate: true
                        });
                    } else {
                        this.map.fitBounds(bounds, this.state.padding);
                    }
                }
                else {
                    // The user location & the nearest stores should be in viewport
                    this.map.fitBounds(bounds, this.state.padding, true);
                }
            } else {
                if (this.storeMarkers.length > 0) {
                    this.storeMarkers.forEach((marker) => {
                        marker.setMap(null);
                    });
                    this.storeMarkers = [];
                }
            }
        }
    }

    loadInitialMarkers(stores: woosmap.map.stores.StoreResponse[], fit = true): void {
        if (stores.length === 0) return;
        this.removeBoundsListener();
        if (this.storeMarkers.length > 0) {
            this.storeMarkers.forEach((marker) => marker.setMap(null));
            this.storeMarkers = [];
        }
        stores.forEach((element) => this.renderStoreMarker(element));
        if (!fit) return;
        const bounds = new woosmap.map.LatLngBounds();
        stores.forEach((feature) => {
            const LatLngPoint = feature.geometry as woosmap.map.GeoJSONPoint;
            bounds.extend({ lat: LatLngPoint.coordinates[1], lng: LatLngPoint.coordinates[0] });
        });
        this.map.fitBounds(bounds, this.state.padding);
    }

    unselectStoreOnDataOverlay(): void {
        this.resetToDefaultIcon();
    }

    selectStoreOnDataOverlay(store?: AssetFeatureResponse): void {
        const selectedStore = store ? JSON.parse(JSON.stringify(store)) : JSON.parse(JSON.stringify(this.state.selectedStore));
        if (this.storeMarkers && this.storeMarkers.length > 0) {
            this.setStoreIcon(selectedStore);
        }
    }

    selectStore(): void {
        if (this.state.selectedStore) {
            const visibleBounds = this.map.getBounds(this.state.padding);
            const selectedStore = this.state.selectedStore;
            const latLng: woosmap.map.LatLngLiteral = {
                lng: selectedStore.geometry.coordinates[0],
                lat: selectedStore.geometry.coordinates[1],
            };
            if (latLng) {
                if (!visibleBounds.contains(latLng)) {
                    this.map.panTo(latLng, this.state.padding);
                }
            }
            this.setStoreIcon(this.state.selectedStore)
        }
    }
    setStoreIcon(store: AssetFeatureResponse): void {
        const type = store.properties?.types?.[0] ?? "JR";
        if (this.storeMarkers && this.storeMarkers.length > 0) {
            this.storeMarkers.forEach(element => {
                element.setIcon(getMarkerIcon(type, false,
                    element.get("cluster") as string | undefined,
                    element.get("kLUB") as boolean | undefined));
            });
            const markerToStyle = this.storeMarkers.find(
                (m) => m && m.get("store_id") === store.properties.store_id,
            );
            if (markerToStyle) {
                this.selectedStoreMarker = markerToStyle;
                markerToStyle.setIcon(getMarkerIcon(type, true,
                    markerToStyle.get("cluster") as string | undefined,
                    markerToStyle.get("kLUB") as boolean | undefined));
            }
        }
    }
    resetToDefaultIcon(): void {
        if (this.storeMarkers && this.storeMarkers.length > 0) {
            this.storeMarkers.forEach(element => {
                element.setIcon(getMarkerIcon(element.get("brand"), false,
                    element.get("cluster") as string | undefined,
                    element.get("kLUB") as boolean | undefined));
            });
        }
    }

    centerMapToUserPosition(): void {

        if (this.map && this.state.zoom && this.state.userCurrentPosition) {
            this.$element.style.pointerEvents = "none";
            this.map.flyTo({
                center: this.state.userCurrentPosition,
                zoom: this.state.zoom,
                padding: this.state.padding,
                animate: true,
                duration: 3000,
            });
            setTimeout(() => {
                this.$element.style.pointerEvents = "";
            }, 3000);

        }
    }
    handleUnSelectedStore(store: AssetFeatureResponse): void {
        this.state.selectedStore = undefined;
        this.emit(MapComponentEvents.STORE_UNSELECTED, this.state.stores);
    }
    disableUserInteraction(): void {
        this.$element.style.pointerEvents = "none";
    }
    enableUserInteraction(): void {
        setTimeout(() => {
            this.$element.style.pointerEvents = "";
        }, 3000);
    }
    showSingleStoreMarker(store: AssetFeatureResponse): void {
        const alreadyShown =
            this.storeMarkers.length === 1 &&
            this.storeMarkers[0].get("store_id") === store.properties.store_id;
        if (alreadyShown) return;     
        this.hideSingleStoreMarker();
        this.renderStoreMarker(store);
    }

    hideSingleStoreMarker(): void {
        this.storeMarkers.forEach((marker) => marker.setMap(null));
        this.storeMarkers = [];
    }
    removeBoundsListener(): void {
        this.bounds_listener.remove();
    }
    panMap(direction: string): void {
        if (this.map) {
            const offset = 100;
            switch (direction) {
                case 'up': this.map.panBy(0, -offset); break;
                case 'down': this.map.panBy(0, offset); break;
                case 'left': this.map.panBy(-offset, 0); break;
                case 'right': this.map.panBy(offset, 0); break;
            }
        }
    }
    onStoreMarkerClicked(marker: woosmap.map.Marker, type: string, store: AssetFeatureResponse): void {
        const cluster = marker.get("cluster") as string | undefined;
        const klub = marker.get("kLUB") as boolean | undefined;
        marker.addListener('click', () => {
            const isSameMarker = this.selectedStoreMarker === marker;
            if (isSameMarker) {
                marker.setIcon(getMarkerIcon(type, false, cluster, klub));
                this.selectedStoreMarker = undefined;
                if (this.state.selectedStore) {
                    this.handleUnSelectedStore(this.state.selectedStore);
                }
            }
            else {
                if (this.selectedStoreMarker) {
                    this.selectedStoreMarker.setIcon(getMarkerIcon(
                        this.selectedStoreMarker.get("brand") as string,
                        false,
                        this.selectedStoreMarker.get("cluster") as string | undefined,
                        this.selectedStoreMarker.get("kLUB") as boolean | undefined));
                    if (this.state.selectedStore) {
                        this.handleUnSelectedStore(this.state.selectedStore);
                    }
                }
                this.selectedStoreMarker = marker;
                marker.setIcon(getMarkerIcon(type, true, cluster, klub));
                this.handleSelectedStore(store);
            }
        })
    }
}
